import React, { useState } from "react"
import Box from "@mui/material/Box"
import { Typography } from "@mui/material"
import ButtonWithIcon from "../Button/Button"
import { ReactComponent as PlusSquareIcon } from "src/v2/assets/icons/plus-square-icon.svg"
import { ReactComponent as FileIcon } from "src/v2/assets/icons/file-icon.svg"
import { ReactComponent as DeleteIcon } from "src/v2/assets/icons/close-square-icon.svg"
import { ReactComponent as CheckIcon } from "src/v2/assets/icons/check-icon.svg"

import PropTypes from "prop-types"

const UploadComponent = ({
    fileInputRef,
    onFileSelect,
    onSendFile,
    compact = false,
    variant = "default",
    accept = ".csv, .pdf, .doc, .docx, .xlsx, .xls, .ptx, .ppt",
    buttonLabel = "ENVIAR ARQUIVO",
    helperText = "Ou arraste e solte os arquivos aqui",
    formatHint,
    validateFile = () => true,
    onFileRejected,
    isSending = false,
    sendLabel = "ENVIAR",
}) => {
    const [selectedFile, setSelectedFile] = useState(null)
    const [isDragging, setIsDragging] = useState(false)
    const isGuided = variant === "guided"

    const trySelectFile = (file) => {
        if (!file || isSending) return false

        if (!validateFile(file)) {
            onFileRejected?.(file)
            return false
        }

        setSelectedFile(file)
        onFileSelect?.(file)
        return true
    }

    const handleFileChange = (event) => {
        if (!trySelectFile(event.target.files[0])) {
            event.target.value = ""
        }
    }

    const handleButtonClick = (event) => {
        event?.stopPropagation()

        if (!isSending && fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const handleDragEnter = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
    }

    const handleDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)

        trySelectFile(e.dataTransfer.files[0])
    }

    const handleDragOver = (e) => {
        e.preventDefault()
    }

    const handleClickDeleteButton = () => {
        if (isSending) return

        setSelectedFile(null)

        if (fileInputRef.current) {
            fileInputRef.current.value = ""
        }

        if (onFileSelect) {
            onFileSelect(null)
        }
    }

    if (selectedFile) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                        gap: 2,
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <FileIcon />

                    <Typography
                        color="common.white"
                        variant="textBody"
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {selectedFile && selectedFile.name}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexShrink: 0,
                    }}
                >
                    {onSendFile && (
                        <ButtonWithIcon
                            size="sm"
                            icon={<CheckIcon />}
                            onClick={() => {
                                if (!isSending) onSendFile(selectedFile)
                            }}
                            disabled={isSending}
                        >
                            {sendLabel}
                        </ButtonWithIcon>
                    )}

                    <ButtonWithIcon
                        icon={<DeleteIcon />}
                        onClick={handleClickDeleteButton}
                        disabled={isSending}
                    >
                        EXCLUIR
                    </ButtonWithIcon>
                </Box>
            </Box>
        )
    }

    return (
        <Box
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={isGuided ? handleButtonClick : undefined}
            data-testid="upload-dropzone"
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                border: isDragging
                    ? "2px dashed #E3A404"
                    : "1px dashed #FFFFFF",
                borderRadius: isGuided || compact ? "12px" : 0,
                paddingY: isGuided ? { xxxs: 3, sm: 4 } : compact ? "20px" : "40px",
                paddingX: isGuided ? { xxxs: 2, sm: 4 } : compact ? "32px" : 0,
                minHeight: isGuided ? { xxxs: 192, sm: 208 } : undefined,
                cursor: isGuided && !isSending ? "pointer" : "default",
                transition: "border-color 0.2s ease, background-color 0.2s ease",
            }}
        >
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
                accept={accept}
                data-testid="upload-file-input"
                disabled={isSending}
            />

            <Box
                sx={{
                    display: "flex",
                    width: isGuided ? "100%" : "fit-content",
                    whiteSpace: isGuided ? "normal" : "nowrap",
                    flexDirection: isGuided || !compact ? "column" : "row",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: isGuided ? 1 : compact ? 3 : 2,
                    textAlign: "center",
                }}
            >
                {isGuided && (
                    <Typography variant="subtitle1" color="common.white" sx={{ fontWeight: 600 }}>
                        Arraste e solte seu arquivo aqui
                    </Typography>
                )}

                <Typography variant="body2" color={isGuided ? "primary.white70" : "common.white"}>
                    {helperText}
                </Typography>

                <ButtonWithIcon
                    size={compact || isGuided ? "md" : "lg"}
                    icon={<PlusSquareIcon />}
                    onClick={handleButtonClick}
                    disabled={isSending}
                >
                    {buttonLabel}
                </ButtonWithIcon>

                {isGuided && formatHint && (
                    <Typography variant="caption" color="primary.white70">
                        {formatHint}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

UploadComponent.propTypes = {
    fileInputRef: PropTypes.object.isRequired,
    onFileSelect: PropTypes.func,
    onSendFile: PropTypes.func,
    compact: PropTypes.bool,
    variant: PropTypes.oneOf(["default", "guided"]),
    accept: PropTypes.string,
    buttonLabel: PropTypes.string,
    helperText: PropTypes.string,
    formatHint: PropTypes.string,
    validateFile: PropTypes.func,
    onFileRejected: PropTypes.func,
    isSending: PropTypes.bool,
    sendLabel: PropTypes.string,
}

export default UploadComponent
